// Clips Store - Audio clips list and search state management
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AudioClip, Actor, Tag, PaginationMeta } from '../api/types';
import clipsApi from '../api/clips';

export const useClipsStore = defineStore('clips', () => {
  // State
  const clips = ref<AudioClip[]>([]);
  const actors = ref<Actor[]>([]);
  const tags = ref<Tag[]>([]);
  const searchQuery = ref('');
  const searchLanguage = ref<'tam' | 'eng'>('tam');
  const selectedActorId = ref<number | null>(null);
  const selectedTag = ref<string | null>(null);  // Default to 'All' (no filter)
  const pagination = ref<PaginationMeta>({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Favorites state
  const favorites = ref<AudioClip[]>([]);
  const favoritesPagination = ref<PaginationMeta>({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const favoritesLoading = ref(false);

  // Getters
  const hasMoreClips = computed(() => pagination.value.page < pagination.value.total_pages);
  const totalClips = computed(() => pagination.value.total);

  // Actions
  async function fetchClips(append = false): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const { clips: fetchedClips, meta } = await clipsApi.listClips({
        q: searchQuery.value || undefined,
        actor_id: selectedActorId.value ?? undefined,
        tag: selectedTag.value ?? undefined,
        page: append ? pagination.value.page + 1 : 1,
        per_page: pagination.value.per_page,
      });

      if (append) {
        clips.value = [...clips.value, ...fetchedClips];
      } else {
        clips.value = fetchedClips;
      }
      pagination.value = meta;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch clips';
    } finally {
      loading.value = false;
    }
  }

  async function fetchActors(): Promise<void> {
    try {
      actors.value = await clipsApi.listActors();
    } catch (e) {
      console.error('Failed to fetch actors:', e);
    }
  }

  async function fetchTags(): Promise<void> {
    try {
      tags.value = await clipsApi.listTags();
    } catch (e) {
      console.error('Failed to fetch tags:', e);
    }
  }

  async function fetchFavorites(page = 1): Promise<void> {
    favoritesLoading.value = true;

    try {
      const { clips: fetchedFavorites, meta } = await clipsApi.getFavorites(page);
      favorites.value = fetchedFavorites;
      favoritesPagination.value = meta;
    } catch (e) {
      console.error('Failed to fetch favorites:', e);
    } finally {
      favoritesLoading.value = false;
    }
  }

  async function toggleFavorite(clipId: number): Promise<void> {
    try {
      const isFavorite = await clipsApi.toggleFavorite(clipId);
      
      // Update clip in clips list
      const clip = clips.value.find(c => c.id === clipId);
      if (clip) {
        clip.is_favorite = isFavorite;
      }

      // Update favorites list
      if (!isFavorite) {
        favorites.value = favorites.value.filter(c => c.id !== clipId);
      }
    } catch (e) {
      console.error('Failed to toggle favorite:', e);
    }
  }

  function setSearchQuery(query: string): void {
    searchQuery.value = query;
  }

  function setSearchLanguage(lang: 'tam' | 'eng'): void {
    searchLanguage.value = lang;
  }

  function setSelectedActor(actorId: number | null): void {
    selectedActorId.value = actorId;
    selectedTag.value = null; // Clear tag when selecting actor
  }

  function setSelectedTag(tag: string | null): void {
    selectedTag.value = tag;
    selectedActorId.value = null; // Clear actor when selecting tag
  }

  function clearFilters(): void {
    searchQuery.value = '';
    selectedActorId.value = null;
    selectedTag.value = null;
  }

  async function loadMore(): Promise<void> {
    if (hasMoreClips.value && !loading.value) {
      await fetchClips(true);
    }
  }

  // Admin actions for actors
  async function createActor(name: string, nameTamil?: string): Promise<void> {
    try {
      await clipsApi.createActor({ name, name_tamil: nameTamil });
      await fetchActors();
    } catch (e) {
      console.error('Failed to create actor:', e);
      throw e;
    }
  }

  async function updateActor(id: number, name: string, nameTamil?: string): Promise<void> {
    try {
      await clipsApi.updateActor(id, { name, name_tamil: nameTamil });
      await fetchActors();
    } catch (e) {
      console.error('Failed to update actor:', e);
      throw e;
    }
  }

  async function deleteActor(id: number): Promise<void> {
    try {
      await clipsApi.deleteActor(id);
      await fetchActors();
    } catch (e) {
      console.error('Failed to delete actor:', e);
      throw e;
    }
  }

  // Admin actions for tags
  async function createTag(name: string): Promise<void> {
    try {
      await clipsApi.createTag({ name });
      await fetchTags();
    } catch (e) {
      console.error('Failed to create tag:', e);
      throw e;
    }
  }

  async function updateTag(id: number, name: string): Promise<void> {
    try {
      await clipsApi.updateTag(id, { name });
      await fetchTags();
    } catch (e) {
      console.error('Failed to update tag:', e);
      throw e;
    }
  }

  async function deleteTag(id: number): Promise<void> {
    try {
      await clipsApi.deleteTag(id);
      await fetchTags();
    } catch (e) {
      console.error('Failed to delete tag:', e);
      throw e;
    }
  }

  // Update a clip's actor, tags, or title
  async function updateClip(clipId: number, data: { actor_id?: number; tag_ids?: number[]; title?: string }): Promise<void> {
    try {
      const updatedClip = await clipsApi.updateClip(clipId, data);
      // Update the clip in the local list
      const index = clips.value.findIndex(c => c.id === clipId);
      if (index !== -1) {
        clips.value[index] = updatedClip;
      }
      // Also update in favorites if present
      const favIndex = favorites.value.findIndex(c => c.id === clipId);
      if (favIndex !== -1) {
        favorites.value[favIndex] = updatedClip;
      }
    } catch (e) {
      console.error('Failed to update clip:', e);
      throw e;
    }
  }

  return {
    // State
    clips,
    actors,
    tags,
    searchQuery,
    searchLanguage,
    selectedActorId,
    selectedTag,
    pagination,
    loading,
    error,
    favorites,
    favoritesPagination,
    favoritesLoading,
    // Getters
    hasMoreClips,
    totalClips,
    // Actions
    fetchClips,
    fetchActors,
    fetchTags,
    fetchFavorites,
    toggleFavorite,
    setSearchQuery,
    setSearchLanguage,
    setSelectedActor,
    setSelectedTag,
    clearFilters,
    loadMore,
    // Admin actions
    createActor,
    updateActor,
    deleteActor,
    createTag,
    updateTag,
    deleteTag,
    updateClip,
  };
});

export default useClipsStore;
